/* src/store/modules/substitute.js */
import TimeFormatter from "@/TimeFormatter";

const Sort = {
    /**
     * Rendezési segédfüggvény:
     * Elsődlegesen a teljes játékidő (totalGameTime) szerint rendez,
     * másodlagosan a kliens oldali időbélyeg (clientTime) szerint.
     */
    compareTotalGameTimeAndDate: function(a, b) {
        if (a.totalGameTime < b.totalGameTime) return -1;
        if (a.totalGameTime > b.totalGameTime) return 1;
        if (a.clientTime < b.clientTime) return -1;
        if (a.clientTime > b.clientTime) return 1;
        return 0;
    }
};

const state = {
    playerPlayTime: {}, // Játékosok pályán töltött ideje (perc:másodperc)
    allItems: [],       // A "Mester" lista: minden esemény minden félidőből (a szűrések elkerülése végett)
    timelineData: [],   // Az aktuális félidő megjelenítendő eseményei (ez látszik a felületen)
    officialExcludes: { // Hivatalos személyek kiállításai (csapatonként)
        A: [],
        B: [],
    },
    storeExcludes: [],  // Átmeneti tároló a kiállításokhoz
    currentPeriod: '1', // Jelenleg kiválasztott félidő/periódus
};

const mutations = {
    SET_PLAYER_PLAYTIME(state, payload) { state.playerPlayTime = payload; },
    SET_ALL_ITEMS(state, items) { state.allItems = items; },
    SET_TIMELINE_DATA(state, items) { state.timelineData = items; },
    SET_OFFICIAL_EXCLUDES(state, payload) {
        if (payload.team && payload.excludes) state.officialExcludes[payload.team] = payload.excludes;
        else state.officialExcludes = payload;
    },
    SET_CURRENT_PERIOD(state, period) { state.currentPeriod = String(period); },
    RESET_STATE(state) {
        state.playerPlayTime = {}; state.allItems = []; state.timelineData = [];
        state.officialExcludes = { A: [], B: [] }; state.currentPeriod = '1';
    },

    // --- CRUD MUTÁCIÓK ---

    /**
     * Hozzáad egy új eseményt a látható idővonalhoz (timelineData),
     * de csak akkor, ha az UUID alapján még nem létezik.
     */
    ADD_TIMELINE_EVENT(state, event) {
        if (!state.timelineData.find(e => e.UUID === event.UUID)) {
            state.timelineData.push(event);
        }
    },

    /**
     * Frissít egy meglévő eseményt a látható idővonalon UUID alapján.
     * Reaktív módon cseréli az objektumot.
     */
    UPDATE_TIMELINE_ITEM(state, event) {
        const index = state.timelineData.findIndex(e => e.UUID === event.UUID);
        if (index !== -1) {
            state.timelineData.splice(index, 1, { ...state.timelineData[index], ...event });
        }
    },

    /**
     * Eltávolít egy eseményt a látható idővonalról (pl. sikertelen mentés visszavonása).
     */
    REMOVE_TIMELINE_ITEM(state, uuid) {
        state.timelineData = state.timelineData.filter(e => e.UUID !== uuid);
    },

    /**
     * Hozzáad egy eseményt a háttérben lévő "Mester" listához (allItems).
     * Ez biztosítja, hogy periódusváltáskor ne vesszen el az új adat.
     */
    ADD_TO_ALL_ITEMS(state, event) {
        if (!state.allItems.find(e => e.UUID === event.UUID)) {
            state.allItems.push(event);
        }
    },

    /**
     * Frissít egy eseményt a "Mester" listában (allItems).
     */
    UPDATE_IN_ALL_ITEMS(state, event) {
        const index = state.allItems.findIndex(e => e.UUID === event.UUID);
        if (index !== -1) {
            state.allItems.splice(index, 1, { ...state.allItems[index], ...event });
        }
    }
};

const actions = {
    /**
     * Inicializálja az adatokat a szerverről vagy adatbázisból érkező lista alapján.
     * "Smart Merge" (Okos összefésülés) logikát alkalmaz:
     * Összehasonlítja a lokális és a bejövő adatokat időbélyeg (clientTime) alapján.
     * Ha a lokális adat frissebb (pl. offline módosítás), azt tartja meg a szerver adata helyett.
     */
    initSubstituteData({ commit, state }, { substituteTimeline, period }) {
        if (!substituteTimeline) return;

        //console.log("--- [DEBUG: STORE INIT START] ---");

        let currentAllItems = [...state.allItems];
        let incomingItems = Array.isArray(substituteTimeline) ? substituteTimeline : [];
        let mergedMap = new Map();

        // Lokális adatok betöltése
        currentAllItems.forEach(item => {
            if (item.UUID) mergedMap.set(item.UUID, item);
        });

        incomingItems.forEach(incomingItem => {
            if (!incomingItem.UUID) return;

            if (mergedMap.has(incomingItem.UUID)) {
                let localItem = mergedMap.get(incomingItem.UUID);
                let localTime = localItem.clientTime || 0;
                let serverTime = incomingItem.clientTime || 0;

                //console.log(`[DEBUG MERGE] Checking Item: ${localItem.action} (${localItem.UUID.substring(0, 8)}...)`);
                //console.log(`   -> Local Time: ${localTime}`);
                //console.log(`   -> Server Time: ${serverTime}`);

                if (serverTime > localTime) {
                    //console.log("   -> DÖNTÉS: SZERVER ADAT NYERT (Felülírás)");
                    mergedMap.set(incomingItem.UUID, incomingItem);
                } else {
                    //console.log("   -> DÖNTÉS: LOKÁLIS ADAT MEGTARTVA (Frissebb vagy egyenlő)");
                }
            } else {
                //console.log(`[DEBUG MERGE] Új adat a szerverről: ${incomingItem.action}`);
                mergedMap.set(incomingItem.UUID, incomingItem);
            }
        });

        let finalAllItems = Array.from(mergedMap.values());

        //console.log(`[DEBUG STORE] Final item count: ${finalAllItems.length}`);

        commit('SET_ALL_ITEMS', finalAllItems);

        let currentPeriodItems = finalAllItems.filter(item =>
            !item.deleted && item.period === period
        );

        commit('SET_TIMELINE_DATA', currentPeriodItems);
        //console.log("--- [DEBUG: STORE INIT END] ---");
    },

    /**
     * Kiszámolja az egyes játékosok pályán töltött idejét.
     * Végigmegy az eseményeken időrendben, és összegzi a "Felmegy" és "Lejön"
     * események közötti időtartamokat.
     */
    calculatePlaytime({ commit, state, rootState }, { items, currentTotalTime, currentPeriod, saveToDb }) {
        if (!items || !items.length) return;

        let activePeriod = state.currentPeriod;
        if (currentPeriod !== undefined && currentPeriod !== null) {
            activePeriod = String(currentPeriod);
            commit('SET_CURRENT_PERIOD', activePeriod);
        }

        let sortedItems = [...items].sort(Sort.compareTotalGameTimeAndDate);
        let playerPlayTimeTemp = {};

        // Ha nincs megadva aktuális idő (pl. meccs vége), akkor az utolsó esemény ideje a mérvadó
        let maxEventTime = sortedItems.length ? sortedItems[sortedItems.length - 1].totalGameTime : 0;
        let effectiveCurrentTime = (currentTotalTime > 0) ? currentTotalTime : maxEventTime;

        const allowedActions = ['Kezdőjátékos', 'Felmegy', 'Csere', 'Kiállítás', 'Hivatalos Kiállítás', '2perc', '4perc', 'kiallitas', 'piroslap'];

        sortedItems.forEach(item => {
            if (allowedActions.includes(item.action) && !item.deleted) {
                let p = String(item.period);
                let time = item.totalGameTime;

                if (!playerPlayTimeTemp[p]) playerPlayTimeTemp[p] = {};

                // Játékos felmegy a pályára
                if (item.up && item.up.id) {
                    let pid = String(item.up.id);
                    if (!playerPlayTimeTemp[p][pid]) playerPlayTimeTemp[p][pid] = { up: [], down: [] };
                    let entry = playerPlayTimeTemp[p][pid];
                    // JAVÍTÁS (33. tétel): az összegzés-alapú számítás ("minden fel
                    // esemény idejének összege mínusz minden le esemény idejének
                    // összege") KIZÁRÓLAG akkor helyes, ha a fel/le események
                    // szigorúan, felváltva váltják egymást. Egy duplikáltan
                    // rögzített csere (pl. két, néhány másodperc különbséggel
                    // egymást követő, AZONOS irányú "X fel, Y le" bejegyzés) egy
                    // páratlan, pár nélküli bejegyzést hagy hátra, ami a MECCS
                    // TELJES HÁTRALÉVŐ RÉSZÉRE elcsúsztatja mindkét érintett
                    // játékos számított idejét (l. valós eset: 32021422854,
                    // Sretenovic Stevan drasztikusan alul-, Bóka Bendegúz a
                    // félidő hosszánál is TÖBBRE túlszámolva). Itt ezért csak
                    // akkor vesszük figyelembe egy "fel" bejegyzést, ha a
                    // játékos EDDIGI ismert állapota (ugyanennyi vagy több "le",
                    // mint "fel") szerint valóban NINCS a pályán - egy duplikált,
                    // már "fenti" állapotra érkező "fel" eseményt egyszerűen
                    // figyelmen kívül hagyunk, ahelyett hogy hibás párosítást
                    // hozna létre.
                    let isCurrentlyOn = entry.up.length > entry.down.length;
                    if (!isCurrentlyOn) {
                        entry.up.push(time);
                    }
                }

                // Játékos lejön a pályáról (csere vagy kiállítás miatt)
                let downId = null;
                if (item.action !== 'Felmegy' && item.down && item.down.id) {
                    downId = item.down.id;
                }
                else if (['2perc', '4perc', 'kiallitas', 'piroslap'].includes(item.action) && item.params && item.params.selectedPlayer) {
                    downId = item.params.selectedPlayer;
                }

                if (downId) {
                    let pid = String(downId);
                    if (!playerPlayTimeTemp[p][pid]) playerPlayTimeTemp[p][pid] = { up: [], down: [] };
                    let entry = playerPlayTimeTemp[p][pid];
                    // JAVÍTÁS (33. tétel): l. a fenti "fel" ág azonos magyarázata -
                    // csak akkor vesszük figyelembe a "le" bejegyzést, ha a
                    // játékos eddigi állapota szerint valóban A PÁLYÁN VAN (több
                    // "fel", mint "le"); egy duplikált "le" eseményt (a játékos
                    // már amúgy is lent van) figyelmen kívül hagyunk.
                    let isCurrentlyOn = entry.up.length > entry.down.length;
                    if (isCurrentlyOn) {
                        entry.down.push(time);
                    }
                }
            }
        });

        // Összegzés és formázás
        let finalPlayTime = {};

        for (const [periodKey, players] of Object.entries(playerPlayTimeTemp)) {
            for (const [playerId, times] of Object.entries(players)) {

                let ups = times['up'] || [];
                let downs = times['down'] || [];

                let sumUp = ups.reduce((a, b) => a + b, 0);
                let sumDown = downs.reduce((a, b) => a + b, 0);

                let elapsed = sumDown - sumUp;

                // Ha többször ment fel, mint le (tehát még pályán van)
                if (ups.length > downs.length) {
                    if (String(periodKey) === activePeriod) {
                        // Ha ez az aktuális félidő, hozzáadjuk az eddig eltelt időt
                        if (effectiveCurrentTime > 0) {
                            elapsed += effectiveCurrentTime;
                        }
                    }
                    else {
                        // Ha egy korábbi félidő, akkor a félidő végéig számolunk
                        let periodDef = null;
                        if (rootState.game && rootState.game.gamedata && rootState.game.gamedata.periods) {
                            periodDef = rootState.game.gamedata.periods.find(p => p.value === String(periodKey));
                        }

                        let periodEndTime = 0;
                        if (periodDef) {
                            let start = periodDef.startat ? parseInt(periodDef.startat) : 0;
                            let duration = periodDef.duration ? parseInt(periodDef.duration) : 1800;
                            periodEndTime = start + duration;
                        } else {
                            if (String(periodKey) === "1") periodEndTime = 1800; // Fallback
                        }

                        if (periodEndTime === 0 && effectiveCurrentTime > 0) {
                            periodEndTime = effectiveCurrentTime;
                        }
                        elapsed += periodEndTime;
                    }
                }

                elapsed = Math.max(0, elapsed);

                if (typeof finalPlayTime[playerId] === "undefined") {
                    finalPlayTime[playerId] = {
                        elapsed: elapsed,
                        elapsedString: TimeFormatter.intToString(elapsed)
                    }
                } else {
                    finalPlayTime[playerId].elapsed += elapsed;
                    finalPlayTime[playerId].elapsedString = TimeFormatter.intToString(finalPlayTime[playerId].elapsed);
                }
            }
        }

        commit('SET_PLAYER_PLAYTIME', finalPlayTime);

        if (saveToDb) {
            window.dbmanager.write({
                action: 'playtime',
                params: { playtime: finalPlayTime }
            }, true);
        }
    },

    /**
     * Új esemény létrehozása (pl. csere, büntetés).
     * 1. Hozzáadja a Store-hoz (optimista UI frissítés).
     * 2. Elmenti az adatbázisba a háttérben.
     * 3. Ha a mentés sikertelen, visszavonja a Store-ból.
     */
    async createEvent({ commit }, payload) {
        console.log("ACTION: createEvent", payload);
        const eventData = {
            ...payload,
            UUID: payload.UUID || window.dbmanager.generateUUID(),
            clientTime: Date.now(),
            isNew: false,
            editing: false
        };
        commit('ADD_TIMELINE_EVENT', eventData);
        commit('ADD_TO_ALL_ITEMS', eventData);

        try {
            await window.dbmanager.write(eventData, false);
        } catch (error) {
            console.error("Hiba az esemény mentésekor:", error);
            commit('REMOVE_TIMELINE_ITEM', eventData.UUID);
        }
    },

    /**
     * Esemény logikai törlése (deleted flag beállítása).
     * Frissíti a látható listát (timelineData) és a mester listát (allItems) is.
     */
    async deleteEvent({ commit }, item) {
        console.log("ACTION: deleteEvent", item);
        const updatedItem = { ...item, editing: false, isNew: false, deleted: 1 };
        commit('UPDATE_TIMELINE_ITEM', updatedItem);
        commit('UPDATE_IN_ALL_ITEMS', updatedItem);
        window.dbmanager.update(updatedItem);
    },

    /**
     * Meglévő esemény módosítása (pl. javítás).
     * Szinkronban tartja a timelineData-t és az allItems-t.
     */
    async updateEvent({ commit }, item) {
        console.log("ACTION: updateEvent", item);
        const updatedItem = { ...item, editing: false, isNew: false };
        commit('UPDATE_TIMELINE_ITEM', updatedItem);
        commit('UPDATE_IN_ALL_ITEMS', updatedItem);
        window.dbmanager.update(updatedItem);
    },

    /**
     * Több esemény egyszerre történő mentése/frissítése (pl. félidő váltáskor).
     */
    async saveEvents({ commit }, events) {
        if (!Array.isArray(events)) return;
        events.forEach(item => {
            const updatedItem = { ...item, editing: false, isNew: false };
            commit('UPDATE_TIMELINE_ITEM', updatedItem);
            commit('UPDATE_IN_ALL_ITEMS', updatedItem);
            window.dbmanager.update(updatedItem);
        });
    }
};

const getters = {
    /**
     * Visszaadja egy adott játékos összesített játékidejét (objektum formátumban).
     * Ha nincs adat, 00:00-t ad vissza.
     */
    getPlayerPlayTime: (state) => (playerId) => {
        let pid = String(playerId);
        return state.playerPlayTime[pid] || { elapsed: 0, elapsedString: '00:00' };
    }
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
